<template>
  <AppSidebar>
    <div class="space-y-6 max-w-5xl mx-auto">
      <!-- Page Header -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">My Developer Profile</h2>
          <p class="text-gray-600">Manage your public profile information and skills</p>
        </div>
        <div class="flex items-center gap-3">
          <router-link 
            v-if="profile?.slug"
            :to="'/profile/' + profile.slug" 
            target="_blank"
            class="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Public Profile
          </router-link>
        </div>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>

      <div v-else-if="!profile && !showForm" class="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center">
        <div class="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">Create Your Public Profile</h3>
        <p class="text-gray-600 mb-6 max-w-md mx-auto">You haven't created a developer profile yet. Create one to showcase your skills and experience to potential clients.</p>
        <button @click="showForm = true" class="btn btn-primary px-8 py-3">
          Create Profile Now
        </button>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Sidebar: Image & Stats -->
        <div class="space-y-6">
          <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden text-center">
            <!-- Cover Image -->
            <div class="relative h-32 w-full bg-gradient-to-r from-primary-600 to-purple-600 group">
              <img v-if="profile?.cover_image_url" :src="profile.cover_image_url" class="w-full h-full object-cover opacity-60" />
              <div class="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors"></div>
              <label class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <div class="flex flex-col items-center text-white">
                  <svg class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span class="text-[10px] font-black uppercase tracking-widest">Update Cover</span>
                </div>
                <input type="file" @change="handleImageUpload($event, 'cover')" accept="image/*" class="hidden" />
              </label>
            </div>

            <!-- Profile Image & Info -->
            <div class="p-6 pt-0">
              <div class="relative w-32 h-32 mx-auto -mt-16 mb-4">
                <div class="w-full h-full rounded-2xl overflow-hidden bg-primary-50 border-4 border-white shadow-xl">
                  <img v-if="profile?.profile_image_url" :src="profile.profile_image_url" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex items-center justify-center text-primary-600 text-4xl font-bold">
                    {{ authStore.user?.name?.charAt(0) }}
                  </div>
                </div>
                <label class="absolute -bottom-2 -right-2 w-10 h-10 bg-white rounded-xl shadow-lg border border-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
                  <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input type="file" @change="handleImageUpload($event, 'profile')" accept="image/*" class="hidden" />
                </label>
              </div>
              <h3 class="text-xl font-bold text-gray-900">{{ profile?.name || authStore.user?.name }}</h3>
              <p class="text-sm text-primary-600 font-medium mb-4">{{ profile?.title || 'Expert Developer' }}</p>
              
              <div class="flex items-center justify-center gap-6 py-4 border-t border-gray-100 mt-4">
                <div class="text-center">
                  <p class="text-lg font-bold text-gray-900">{{ profile?.experience_years || 0 }}</p>
                  <p class="text-xs text-gray-500 uppercase tracking-wider">Years</p>
                </div>
                <div class="text-center">
                  <p class="text-lg font-bold text-gray-900">{{ profile?.projects_completed || 0 }}</p>
                  <p class="text-xs text-gray-500 uppercase tracking-wider">Projects</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Visibility Toggle -->
          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-bold text-gray-900">Public Status</h4>
              <span :class="profile?.is_active ? 'text-green-600' : 'text-red-600'" class="text-xs font-bold uppercase">
                {{ profile?.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <p class="text-sm text-gray-500 mb-4">When active, your profile is visible on the About page and your public profile link.</p>
            <button 
              @click="toggleActive" 
              class="w-full py-2 rounded-xl text-sm font-bold transition-all"
              :class="profile?.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'"
            >
              {{ profile?.is_active ? 'Set to Inactive' : 'Set to Active' }}
            </button>
          </div>
        </div>

        <!-- Main Form -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div class="p-8 border-b border-gray-100">
              <h3 class="text-xl font-bold text-gray-900">Edit Profile Details</h3>
            </div>
            
            <form @submit.prevent="handleSubmit" class="p-8 space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Display Name *</label>
                  <input v-model="form.name" type="text" required class="form-input" placeholder="Your exhibition name" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Professional Title *</label>
                  <input v-model="form.title" type="text" required class="form-input" placeholder="e.g. Senior Full Stack" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 mb-1">Bio</label>
                <textarea v-model="form.bio" rows="4" class="form-input" placeholder="Write something amazing about your skills and experience..."></textarea>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Skills (Comma separated)</label>
                  <input v-model="skillsInput" type="text" @blur="updateSkills" class="form-input" placeholder="React, Vue, Node.js" />
                  <div class="mt-2 flex flex-wrap gap-2">
                    <span v-for="skill in form.skills" :key="skill" class="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-full uppercase">
                      {{ skill }}
                    </span>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1">Experience (Yrs)</label>
                    <input v-model.number="form.experience_years" type="number" class="form-input" />
                  </div>
                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1">Projects</label>
                    <input v-model.number="form.projects_completed" type="number" class="form-input" />
                  </div>
                </div>
              </div>

              <div class="pt-6 border-t border-gray-100">
                <h4 class="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Social Media Links</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div v-for="platform in platforms" :key="platform.id" class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
                      <component :is="platform.icon" class="w-4 h-4" />
                    </div>
                    <input v-model="form.social_links[platform.id]" type="url" class="flex-1 form-input text-sm" :placeholder="platform.name + ' URL'" />
                  </div>
                </div>
              </div>

              <div class="pt-8 flex items-center justify-end gap-4">
                <button type="submit" :disabled="saving" class="btn btn-primary px-12 py-3 shadow-lg shadow-primary-200 transform hover:scale-[1.02] active:scale-95 transition-all">
                  <span v-if="saving" class="flex items-center gap-2">
                    <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Saving...
                  </span>
                  <span v-else>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useDevProfilesStore } from '@/stores/devProfiles'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const authStore = useAuthStore()
const devStore = useDevProfilesStore()
const uiStore = useUIStore()

const loading = ref(true)
const saving = ref(false)
const showForm = ref(false)
const skillsInput = ref('')

const platforms = [
  { id: 'linkedin', name: 'LinkedIn', icon: 'LinkedInIcon' },
  { id: 'github', name: 'GitHub', icon: 'GitHubIcon' },
  { id: 'behance', name: 'Behance', icon: 'BehanceIcon' },
  { id: 'dribbble', name: 'Dribbble', icon: 'DribbbleIcon' },
  { id: 'twitter', name: 'Twitter', icon: 'TwitterIcon' },
  { id: 'website', name: 'Website', icon: 'GlobeIcon' }
]

const form = ref({
  name: '',
  title: '',
  bio: '',
  skills: [],
  social_links: {
    linkedin: '',
    github: '',
    behance: '',
    dribbble: '',
    twitter: '',
    website: ''
  },
  experience_years: 0,
  projects_completed: 0,
  is_active: true
})

const profile = computed(() => {
  return devStore.devProfiles.find(p => p.user_id === authStore.user?.id)
})

watch(profile, (newProfile) => {
  if (newProfile) {
    form.value = {
      ...newProfile,
      social_links: {
        linkedin: newProfile.social_links?.linkedin || '',
        github: newProfile.social_links?.github || '',
        behance: newProfile.social_links?.behance || '',
        dribbble: newProfile.social_links?.dribbble || '',
        twitter: newProfile.social_links?.twitter || '',
        website: newProfile.social_links?.website || ''
      }
    }
    skillsInput.value = newProfile.skills ? newProfile.skills.join(', ') : ''
  }
}, { immediate: true })

onMounted(async () => {
  loading.value = true
  await devStore.fetchDevProfiles(true)
  loading.value = false
})

function updateSkills() {
  if (skillsInput.value) {
    form.value.skills = skillsInput.value.split(',').map(s => s.trim()).filter(s => s !== '')
  } else {
    form.value.skills = []
  }
}

async function handleSubmit() {
  updateSkills()
  saving.value = true
  
  const { profile_image, cover_image, profile_image_url, cover_image_url, ...profileData } = form.value
  
  try {
    if (profile.value) {
      await devStore.updateDevProfile(profile.value.id, profileData)
      uiStore.showNotification({ type: 'success', message: 'Profile updated successfully!' })
    } else {
      await devStore.createDevProfile(profileData)
      uiStore.showNotification({ type: 'success', message: 'Profile created successfully!' })
      showForm.value = false
    }
  } catch (error) {
    uiStore.showNotification({ type: 'error', message: 'Failed to save profile' })
  } finally {
    saving.value = false
  }
}

async function handleImageUpload(event, type) {
  if (!profile.value) {
    uiStore.showNotification({ type: 'warning', message: 'Please create your profile first before uploading images.' })
    return
  }

  const file = event.target.files[0]
  if (file) {
    await devStore.uploadDevProfileImage(profile.value.id, file, type)
    uiStore.showNotification({ type: 'success', message: 'Image uploaded successfully!' })
  }
}

async function toggleActive() {
  if (profile.value) {
    await devStore.toggleDevProfileActive(profile.value.id)
  }
}
</script>

<script>
// Mock icons for now
export default {
  components: {
    LinkedInIcon: { template: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>' },
    GitHubIcon: { template: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.042-1.416-4.042-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>' },
    TwitterIcon: { template: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>' },
    BehanceIcon: { template: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M22 14.455c-.013-.397-.043-.761-.137-1.082-.093-.321-.248-.598-.465-.828-.216-.231-.502-.407-.856-.527-.354-.12-.806-.181-1.352-.181-.469 0-.877.058-1.229.175-.351.117-.65.289-.893.518-.244.228-.432.505-.561.833-.131.328-.201.7-.213 1.115h5.706zm-10.334-1.168c0-.462-.061-.853-.18-1.173-.119-.32-.303-.574-.551-.76-.247-.186-.566-.312-.958-.378-.393-.066-.867-.1-1.425-.1H5.341v4.454h3.11c.54 0 .991-.036 1.35-.107.361-.072.658-.198.892-.377.234-.179.406-.425.515-.736.11-.31.164-.678.164-1.104l-.006-.019zm1.144 3.012c-.092.483-.284.904-.576 1.264-.291.36-.677.641-1.157.844-.479.202-1.066.304-1.758.304H5.341V21h3.7c.801 0 1.517-.11 2.131-.328.614-.218 1.127-.534 1.53-.946.403-.414.717-.92.934-1.516.216-.597.324-1.272.324-2.033 0-.613-.105-1.155-.297-1.636-.209-.481-.497-.899-.861-1.25-.365-.352-.801-.635-1.309-.844.5-.231.896-.534 1.187-.909.29-.375.436-.85.436-1.426 0-.585-.084-1.083-.251-1.493-.166-.409-.427-.745-.783-1.002-.354-.257-.803-.442-1.343-.551-.54-.108-1.189-.163-1.946-.163H2.031V21H0V3h8.336c1.077 0 2.016.084 2.812.253.796.168 1.444.437 1.947.804s.872.844 1.107 1.428c.234.584.351 1.284.351 2.099 0 .614-.106 1.15-.316 1.61-.21.46-.516.844-.91 1.15-.393.307-.866.541-1.424.698.63.149 1.147.411 1.554.787.406.376.713.844.921 1.4s.312 1.2.312 1.932c0 .779-.115 1.465-.346 2.051zm11.19-4.88c0-.853-.131-1.614-.393-2.285-.262-.67-.655-1.229-1.173-1.677-.518-.448-1.158-.781-1.916-.998-.758-.218-1.634-.327-2.617-.327-1.011 0-1.897.126-2.66.378s-1.393.593-1.896 1.025c-.503.431-.883.946-1.143 1.545-.259.598-.389 1.25-.389 1.957 0 .741.137 1.408.411 1.999.274.59.664 1.082 1.173 1.472s1.115.694 1.82 1.118c-.689.176-1.236.467-1.635.872-.398.406-.599.946-.599 1.62 0 .341.076.621.222.84.146.216.347.387.604.512.256.124.551.206.883.245.332.039.673.058 1.024.058h9.191V19h-10.45c.219-.344.328-.731.328-1.159 0-.462-.119-.844-.354-1.146-.235-.302-.601-.52-1.096-.653 1.002-.303 1.761-.758 2.279-1.362.518-.605.777-1.373.777-2.302 0-.689-.112-1.3-.33-1.83s-.545-.98-1.066-1.341c.543.013 1.042.02 1.492.02h5.584c.54 0 .979.143 1.309.431.33.287.495.733.495 1.334v.667H24v-.219zm-.656-11.575v2h-6.562v-2h6.562z"/></svg>' },
    DribbbleIcon: { template: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-2.47-.746-4.998-.342.102.26.204.52.298.78.272.766.49 1.58.647 2.44 2.218-.846 3.656-2.072 4.052-2.878zM15.33 19.34c-.144-.805-.353-1.57-.61-2.288-2.528-.15-5.592.516-8.54 2.13a10.057 10.057 0 005.82 2.118v-.308c0-.58.337-1.134.846-1.42.508-.287 1.127-.266 1.622.052.496.317.846.88.862 1.496v.22zM4.14 17.02c2.443-1.41 5.163-2.083 7.625-2.083.337 0 .668.013.993.04a11.164 11.164 0 00-1.87-3.69c-3.15.932-6.502.915-9.458-.046a9.96 9.96 0 002.71 5.78zM1.4 10.034c2.61.764 5.56.74 8.24-.047C8.428 7.398 7.152 4.81 5.86 2.37A9.972 9.972 0 001.4 10.034zm8.01-8.15c1.196 2.176 2.38 4.457 3.328 6.953 2.176-.718 4.14-2.016 5.642-3.763A9.914 9.914 0 0012 1a9.93 9.93 0 00-2.59.884zm9.352 4.8c-1.4 1.633-3.23 2.87-5.266 3.565.1.25.196.505.286.76.27.76.49 1.564.646 2.418.59-.06 1.186-.092 1.785-.092 1.34 0 2.65.16 3.9.475a10.04 10.04 0 00-1.35-7.126z"/></svg>' },
    GlobeIcon: { template: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>' }
  }
}
</script>
